import _ from 'lodash';
import * as React from 'react';
import classNames from 'classnames';

import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, match } from 'react-router-dom';

import * as api from '../../api';
import * as store from '../../reducers/store';
import * as Cache from '../../utils/cache';
import * as FormUtils from '../../utils/form';
import * as FormSetUtils from '../../utils/formSet';
import * as logger from '../../logger';
import * as rootReducer from '../../reducers/root';

import { FormSet } from '../../reducers/store';
import { getFormPath } from '../../utils/form';

import AllergiesPage from './AllergiesPage';
import DocumentSignerPage from './DocumentSignerPage';
import MedicationsPage from './MedicationsPage';
import { Page } from './Page';
import PatientCareProvidersPickerPage from './PatientCareProvidersPickerPage';
import { PhotoIdUploadPage } from './PhotoIdUploadPage';
import SurgicalHistoryPage from './SurgicalHistoryPage';

import LoadingModal from '../../components/LoadingModal';
import MultiInsurance from './InsurancePage/MultiInsurance';
import NavBar, { View } from '../../components/NavBar';
import Payments from '../Payments';
import { StaticStatus } from '../../components/Question';
import VirtualVisitPage from './VirtualVisitPage';
import { withFetchPatient } from '../../components/hoc/withFetchPatient';
import { fullPagePrimitive } from '../../components/utils';
import { getIntegerParamFromLocationSearch } from '../../utils/navigation';
import BodyDiagramPage from './BodyDiagramPage';
import FreehandBodyDiagramPage from './FreehandBodyDiagramPage';
import Registration from '../../components/Registration';
import Header from '../Schedule/Form/Header';
import {
  getMaybeFormSetSelectedTimeSlot,
  getMaybeSelectedEhrProviderWithPractice,
  getRegistrationErrorMessage,
  maybeBookSelectedEhrTimeSlots,
} from '../../utils/scheduling';
import InsuranceRouter from './InsurancePage/v2/InsuranceRouter';
import PhotoIdRouter from './PhotoIdUploadPage/v2/PhotoIdRouter';
import ScheduleAppointmentPage from './ScheduleAppointmentPage';
import ScheduleSeriesAppointmentRouter from './ScheduleAppointmentPage/ScheduleSeriesAppointmentRouter';
import { InfoMessage } from '../../components/InfoMessage';

import styles from '../FormStart/index.module.css';
import { parseValue } from 'common/dist/utils/types';
import { SchedulingFormFieldValue } from '../../components/ScheduleAppointment/AppointmentDisplay/common';

const { Primitive } = api.PrevisitFormTemplateFieldConfig;

export interface StateProps {
  practicePreference: api.Previsit.PracticePreference;
  patient: api.Patient.Patient | null;
  documents: api.PrevisitDocument.DocumentWithTemplate[];
  preferredLanguage: store.Language;
  selectedFormSet: FormSet;
  selectedFormId: string;
  selectedFormSetId: string;
  multiFormSet: boolean;
  selectedForm?: api.PrevisitForm.PrevisitForm;
  formSets: store.FormSets;
  authenticationCode: string | null;
  ehrProviderInformation: api.User.EhrProviderInformationPractice[];
  selectedEhrTimeSlot: store.SelectedEhrTimeSlot | null;
  selectedProviderId: string | null;
  provisionalPatientId: string | null;
  isProvisionalPatientReconciled: boolean;
}

export interface DispatchProps {
  setFormField: (formFieldId: string, value: string) => void;
  setFormSets: (
    formSets: api.PrevisitFormSet.PrevisitFromSetWithPatientData[],
    clear?: boolean,
  ) => void;
  setFormStatus: (
    formSetId: string,
    formId: string,
    status: api.PrevisitForm.Status,
  ) => void;
  setDocuments: (
    formSetId: string,
    documents: api.PrevisitDocument.DocumentWithTemplate[],
  ) => void;
  setSelectedEhrTimeSlot: (
    selectedEhrTimeSlot: store.SelectedEhrTimeSlot | null,
  ) => void;
  setBookedSlot: (
    bookedSlot: api.EhrTimeSlot.TimeSlotConfirmation | null,
  ) => void;
}

interface Props
  extends RouteComponentProps,
    StateProps,
    DispatchProps,
    WithTranslation {
  match: match<{
    authenticationCode?: string;
    formId?: string;
  }>;
}

interface State {
  page: number;
  isLoading: boolean;
  errorMsg: string;
}

class FormComponent extends React.Component<Props, State> {
  state: State = { page: 1, isLoading: true, errorMsg: '' };

  componentWillMount() {
    const { location } = this.props;
    const page =
      getIntegerParamFromLocationSearch(location.search, 'page') || 1;
    this.setState({ page });
  }

  componentDidMount() {
    const { match, selectedFormId, selectedFormSetId, selectedForm } =
      this.props;
    const { authenticationCode } = match.params.authenticationCode
      ? match.params
      : { authenticationCode: this.props.authenticationCode };
    if (!selectedFormSetId || !selectedFormId) {
      return;
    }
    if (authenticationCode) {
      Cache.startPrevisitFormSet(selectedFormSetId, authenticationCode);
    }
    const fields = selectedForm?.fields;
    if (fields) {
      const selectedTimeSlot = getMaybeFormSetSelectedTimeSlot(
        fields,
        this.state.page,
      );
      if (selectedTimeSlot) {
        this.props.setSelectedEhrTimeSlot(selectedTimeSlot);
      }
    }
    this.setState({ isLoading: false });
    this.maybeAutoAdvanceOnEmptyPage();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    this.handleLastPageOnUpdate();
    this.maybeAutoAdvanceOnEmptyPage(prevState);
  }

  handleLastPageOnUpdate() {
    const { formSets, location } = this.props;
    const hasLastPage = !!FormSetUtils.getLastPageFormSet(formSets);
    if (hasLastPage) return;
    const urlPage = getIntegerParamFromLocationSearch(location.search, 'page');
    if (!urlPage || urlPage === this.state.page) return;
    if (location.search && this.state.page !== urlPage) {
      this.setState({ page: urlPage });
    }
  }

  maybeAutoAdvanceOnEmptyPage(prevState?: State) {
    const { page, isLoading } = this.state;
    if (
      (page === prevState?.page && isLoading === prevState.isLoading) ||
      isLoading === true
    )
      return;
    const { pageFields } = this.getPageFields(page);
    if (!pageFields.length) {
      this.navigateToNextPage();
      return;
    }
  }

  getPageFields(page: number) {
    const { patient, selectedForm } = this.props;
    const fields = selectedForm?.fields;
    if (!fields) return { pageFields: [], fields: fields || [] };
    const pageFields = fields.filter(
      (f) => f.page === page && !FormUtils.checkIsHidden(f, fields, patient),
    );
    return { pageFields, fields };
  }

  componentWillUnmount() {
    const { selectedFormSetId, match, formSets, documents } = this.props;
    const { authenticationCode } = match.params;
    if (!selectedFormSetId) return;
    const hasLastPage = !!FormSetUtils.getLastPageFormSet(formSets);
    if (
      !FormUtils.hasIncompleteForm(
        formSets[selectedFormSetId]?.forms || {},
        documents,
      ) ||
      hasLastPage
    ) {
      this.clearHiddenFields();
      if (authenticationCode) {
        this.debouncedSubmitFormSet(selectedFormSetId, authenticationCode);
      }
    }
  }

  debouncedSubmitFormSet = _.debounce(
    (formSetId: string, authenticationCode: string) => {
      api.client.Previsit.submitFormSet(formSetId, authenticationCode);
    },
    1000,
    { leading: true },
  );

  handleLastPage = async (formSetId: string, formFieldId: string) => {
    const { setFormField } = this.props;
    await api.client.Previsit.updateFormField(
      formSetId,
      formFieldId,
      StaticStatus.LastPage,
    );
    setFormField(formFieldId, StaticStatus.LastPage);
    await this.exitForm();
  };

  clearHiddenFields = async () => {
    const { selectedFormSet, patient, setFormField } = this.props;
    const forms = selectedFormSet.forms;
    const formFields = _.flatten(
      Object.values(forms).map((form) => form.fields),
    );
    const hiddenWithValue = formFields.filter(
      (formField) =>
        formField.value &&
        FormUtils.checkIsHidden(formField, formFields, patient),
    );
    if (!hiddenWithValue.length) return;
    try {
      await Promise.all(
        hiddenWithValue.map(
          async (hidden) =>
            (await api.client.Previsit.updateFormField(
              hidden.previsitFormSetId,
              hidden.id,
              '',
            )) && setFormField(hidden.id, ''),
        ),
      );
    } catch (e) {
      logger.error(
        e,
        `Failed to clear hidden form fields ${hiddenWithValue
          .map((value) => value.id)
          .join(', ')}`,
      );
    }
  };

  navigateToNextPageWithContent = () => {
    const { page } = this.state;
    const { patient } = this.props;
    const fields = this.props.selectedForm?.fields ?? [];
    let nextPageWithContent = page + 1;
    const maxPage = Math.max(
      ...(fields.map((field) => field.page) || [page + 1]),
    );
    for (let nextPage = page + 1; nextPage <= maxPage; nextPage++) {
      const visibleFields = fields.filter(
        (field) =>
          field.page === nextPage &&
          !FormUtils.checkIsHidden(field, fields, patient),
      ).length;
      if (visibleFields) {
        nextPageWithContent = nextPage;
        break;
      }
    }
    if (nextPageWithContent > maxPage) {
      return this.exitForm();
    }
    const queryParams = new URLSearchParams(
      this.props.location.search.slice(1),
    );
    queryParams.set('page', `${nextPageWithContent}`);
    this.props.history.push({
      pathname: FormUtils.getInboundPath(this.props.location.pathname),
      search: queryParams.toString(),
    });
  };

  navigateToPreviousPageWithContent = () => {
    const { page } = this.state;
    const { patient } = this.props;
    const fields = this.props.selectedForm?.fields ?? [];
    let previousPageWithContent = page - 1;
    for (
      let previousPage = page > 1 ? page - 1 : 1;
      previousPage > 0;
      previousPage--
    ) {
      const pageFields = fields.filter(
        (field) =>
          field.page === previousPage &&
          !FormUtils.checkIsHidden(field, fields, patient),
      );
      const isOnlyRegistration =
        pageFields.length === 1 &&
        pageFields[0].primitive === Primitive.Registration;
      if (pageFields.length && !isOnlyRegistration) {
        previousPageWithContent = previousPage;
        break;
      }
    }
    const queryParams = new URLSearchParams(
      this.props.location.search.slice(1),
    );
    queryParams.set('page', `${previousPageWithContent}`);
    this.props.history.push({
      pathname: FormUtils.getInboundPath(this.props.location.pathname),
      search: queryParams.toString(),
    });
  };

  navigateToNextPage = async () => {
    const { page, isLoading } = this.state;
    const {
      history,
      match,
      patient,
      selectedForm,
      multiFormSet,
      selectedFormSetId,
      provisionalPatientId,
    } = this.props;
    this.setState({ errorMsg: '' });
    if (provisionalPatientId) return this.navigateToNextPageWithContent();
    const { authenticationCode, formId } = match.params;
    const fields = selectedForm?.fields ?? [];
    const formHasMoreQuestions = fields.some(
      (f) => f.page > page && !FormUtils.checkIsHidden(f, fields, patient),
    );

    if (formHasMoreQuestions) {
      const pageIsEmpty = !fields.filter(
        (f) => f.page === page && !FormUtils.checkIsHidden(f, fields, patient),
      ).length;
      const nextPage = page + 1;

      if (authenticationCode && formId) {
        // avoid cases where we cannot hit back due to skipping empty pages
        (pageIsEmpty ? history.replace : history.push)({
          pathname: getFormPath({
            authenticationCode,
            formSetId: selectedFormSetId,
            formId,
            multi: multiFormSet,
          }),
          search: `?page=${nextPage}`,
        });
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return;
    }

    if (isLoading) return;

    this.setState({ isLoading: true });

    try {
      // exiting forms should not require setting loading to false
      await this.exitForm();
    } catch (err) {
      // but erroring will, so set isLoading to false with warning
      logger.error(
        new Error(
          `[Form.navigateToNextPage]: failed to exit form: ${err.stack}`,
        ),
      );
      this.setState({ isLoading: false });
    }
  };

  maybeLoadDocuments = async (forms: store.Forms) => {
    const existingDocumentSigner =
      FormUtils.getMaybeDocumentSignerFormField(forms);
    if (!existingDocumentSigner) return;
    const documents =
      await api.client.PrevisitDocumentTemplate.findAllByFormSetAndField(
        existingDocumentSigner.previsitFormSetId,
        existingDocumentSigner.id,
      );
    this.props.setDocuments(
      existingDocumentSigner.previsitFormSetId,
      documents,
    );
  };

  handleAppointmentSelected = async (
    timeSlot: api.EhrTimeSlot.EhrTimeSlot,
    field: api.PrevisitFormField.PrevisitFormFieldWithConfig,
  ): Promise<{ navigateToConfirmation: boolean }> => {
    const { provisionalPatientId, setSelectedEhrTimeSlot, setFormField } =
      this.props;
    let formFieldValue;
    if (provisionalPatientId && !this.props.isProvisionalPatientReconciled) {
      setSelectedEhrTimeSlot({
        ...timeSlot,
        previsitFormTemplateFieldConfigId:
          field.previsitFormTemplateFieldConfigId,
        scheduleAppointmentDisplayBy: field.scheduleAppointmentDisplayBy,
      });
      formFieldValue = JSON.stringify({ selectedAppointment: timeSlot });
    } else {
      const confirmation = await api.client.EhrTimeSlot.bookTimeSlot({
        ehrTimeSlotId: timeSlot.id,
        previsitFormFieldId: field.id,
        ...timeSlot,
      });
      formFieldValue = JSON.stringify({
        selectedAppointment: { ...timeSlot, ...confirmation },
      });
    }
    setFormField(field.id, formFieldValue);
    await api.client.Previsit.updateFormField(
      field.previsitFormSetId,
      field.id,
      formFieldValue,
    );
    return { navigateToConfirmation: !provisionalPatientId };
  };

  handleSeriesAppointmentSelected = async (
    timeSlot: api.EhrTimeSlot.EhrTimeSlot,
    field: api.PrevisitFormField.PrevisitFormFieldWithConfig,
    seriesAppointmentCount?: number,
  ): Promise<{ navigateToConfirmation: boolean }> => {
    const selectedAppointments =
      parseValue<SchedulingFormFieldValue>(field.value)?.selectedAppointments ??
      [];
    const confirmation = await api.client.EhrTimeSlot.bookTimeSlot({
      ehrTimeSlotId: timeSlot.id,
      previsitFormFieldId: field.id,
      ...timeSlot,
    });
    const formFieldValue = JSON.stringify({
      seriesAppointmentCount,
      selectedAppointments: [
        ...selectedAppointments,
        { ...timeSlot, ...confirmation },
      ],
    });
    this.props.setFormField(field.id, formFieldValue);
    await api.client.Previsit.updateFormField(
      field.previsitFormSetId,
      field.id,
      formFieldValue,
    );
    return { navigateToConfirmation: true };
  };

  exitInboundForm = async () => {
    const { practicePreference, history, location } = this.props;
    const queryParams = new URLSearchParams(location.search.slice(1));
    queryParams.delete('page');
    if (practicePreference.collectFeedback) {
      if (history.location.pathname.endsWith('/feedback')) return;
      history.push({
        pathname: `${FormUtils.getInboundPath(
          this.props.location.pathname,
        )}/feedback`,
        search: queryParams.toString(),
      });
    } else {
      if (history.location.pathname.endsWith('/end')) return;
      history.push({
        pathname: `${FormUtils.getInboundPath(
          this.props.location.pathname,
        )}/end`,
        search: queryParams.toString(),
      });
    }
  };

  exitForm = async () => {
    const {
      practicePreference,
      history,
      match,
      patient,
      preferredLanguage,
      selectedFormId,
      setFormStatus,
      multiFormSet,
      setFormSets,
      selectedForm,
      selectedFormSetId,
      provisionalPatientId,
    } = this.props;
    const { authenticationCode } = match.params;
    if (
      selectedForm &&
      selectedFormId &&
      !FormUtils.isDocumentForm(selectedFormId)
    ) {
      await FormUtils.updateFormScore(selectedFormId, selectedForm.fields);
      const completed = await FormUtils.isFormComplete(
        selectedForm,
        selectedFormSetId,
        patient,
        practicePreference,
      );
      const status = completed
        ? api.PrevisitForm.Status.Completed
        : api.PrevisitForm.Status.Started;
      await FormUtils.maybeUpdateFormStatus(selectedFormId, status);
      setFormStatus(selectedFormSetId, selectedFormId, status);
    }
    if (provisionalPatientId) return this.exitInboundForm();
    if (!authenticationCode) return;
    const { formSets } = await api.client.Previsit.fetchFormSets(
      authenticationCode,
      preferredLanguage || undefined,
      multiFormSet,
      true,
    );
    setFormSets(formSets, false);
    await Promise.all(
      formSets.map((formSet) =>
        this.maybeLoadDocuments(formSet.previsitFormSet.forms || {}),
      ),
    );
    await FormUtils.onFormComplete({
      history,
      authenticationCode,
      multiFormSet,
      collectFeedback: practicePreference.collectFeedback,
      formSets: this.props.formSets,
    });
  };

  renderPrimitives = (
    field: api.PrevisitFormField.PrevisitFormFieldWithConfig,
    fields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
    page: number,
  ) => {
    const {
      practicePreference,
      setFormField,
      selectedFormSet,
      documents,
      selectedForm,
      ehrProviderInformation,
      patient,
      selectedProviderId,
    } = this.props;

    const { encounter, provider, practice } = selectedFormSet;
    switch (field.primitive) {
      case Primitive.PatientMedications:
        return (
          <MedicationsPage
            navigateToNextPage={this.navigateToNextPage}
            setFormField={setFormField}
            field={field}
            practicePreference={practicePreference}
            practiceGroupId={practice?.practiceGroupId}
          />
        );
      case Primitive.PatientAllergies:
        return (
          <AllergiesPage
            navigateToNextPage={this.navigateToNextPage}
            field={field}
            setFormField={setFormField}
            practicePreference={practicePreference}
          />
        );
      case Primitive.PatientInsurance:
        if (!encounter) {
          return <></>;
        }
        return (
          <InsuranceRouter
            field={field}
            encounter={encounter}
            navigateToNextPage={this.navigateToNextPage}
            setFormField={setFormField}
          />
        );
      case Primitive.PatientInsuranceCardPhotoUpload:
      case Primitive.PatientInsuranceCardUpload:
        if (!encounter) return <></>;
        return (
          <MultiInsurance
            field={field}
            setFormField={setFormField}
            navigateToNextPage={this.navigateToNextPage}
            encounter={encounter}
          />
        );
      case Primitive.DocumentSigner:
        const flattenForms = _.flatten(_.map(selectedFormSet.forms));
        const formFields = _.flatten(flattenForms.map((form) => form.fields));
        const filteredDocuments = documents.filter((document) =>
          FormUtils.shouldDocumentBeShown(document.conditions, formFields),
        );
        return (
          <DocumentSignerPage
            field={field}
            documents={filteredDocuments}
            practiceName={(practice && practice.name) || ''}
            setFormField={setFormField}
            navigateToNextPage={this.navigateToNextPage}
          />
        );
      case Primitive.BodyDiagram:
        return (
          <BodyDiagramPage
            field={field}
            setFormField={setFormField}
            navigateToNextPage={this.navigateToNextPage}
          />
        );
      case Primitive.FreehandBodyDiagram:
        if (!encounter) return <></>;
        return (
          <FreehandBodyDiagramPage
            field={field}
            setFormField={setFormField}
            navigateToNextPage={this.navigateToNextPage}
            encounter={encounter}
          />
        );
      case Primitive.PatientPhotoId:
        if (!encounter) return <></>;
        return (
          <PhotoIdRouter
            field={field}
            encounter={encounter}
            setFormField={setFormField}
            navigateToNextPage={this.navigateToNextPage}
          />
        );
      case Primitive.PatientPhotoIdUpload:
        if (!encounter) return <></>;
        return (
          <PhotoIdUploadPage
            field={field}
            helpDialog={field.helpDialog}
            encounter={encounter}
            setFormField={setFormField}
            navigateToNextPage={this.navigateToNextPage}
          />
        );
      case Primitive.PatientBillingDetails:
        return (
          <Payments
            practiceName={(practice && practice.name) || ''}
            navigateToNextPage={this.navigateToNextPage}
            field={field}
            setFormField={setFormField}
          />
        );
      case Primitive.Registration:
        const ehrTimeSlotToBook =
          selectedForm?.fields &&
          getMaybeFormSetSelectedTimeSlot(selectedForm.fields);
        return (
          <Registration
            key={field.id}
            formField={field}
            onRegistrationCompleted={async (): Promise<void> => {
              if (!this.props.isProvisionalPatientReconciled) {
                return;
              }
              const bookedSlots = await maybeBookSelectedEhrTimeSlots(
                field.previsitFormSetId,
                fields,
                setFormField,
              );
              bookedSlots.length && this.props.setBookedSlot(bookedSlots[0]);
              return;
            }}
            navigateToNextPage={this.navigateToNextPage}
            practiceId={ehrTimeSlotToBook?.practiceId}
            submitButtonText="Schedule appointment"
            onError={() => {
              if (
                !fields.find(
                  (f) => f.primitive === Primitive.ScheduleAppointment,
                )
              ) {
                return;
              }
              const selectedTimeSlot = selectedForm?.fields
                ? getMaybeFormSetSelectedTimeSlot(
                    selectedForm.fields,
                    page,
                    patient ?? undefined,
                  )
                : null;
              const selectedProviderWithPractice =
                getMaybeSelectedEhrProviderWithPractice(
                  ehrProviderInformation,
                  selectedTimeSlot,
                  selectedProviderId,
                );
              const phoneToCall =
                selectedProviderWithPractice?.practicePhoneNumber ??
                ehrProviderInformation.find(
                  (e) => e.practiceId === selectedTimeSlot?.practiceId,
                )?.practicePhoneNumber;
              const errorMsg = getRegistrationErrorMessage(
                this.props.t,
                phoneToCall,
              );
              this.setState({
                errorMsg,
              });
              this.navigateToPreviousPageWithContent();
            }}
          />
        );
      case Primitive.ScheduleAppointment:
        return (
          <ScheduleAppointmentPage
            formField={field}
            navigateToNextPage={this.navigateToNextPage}
            onAppointmentSelected={async (timeSlot) =>
              await this.handleAppointmentSelected(timeSlot, field)
            }
            practice={practice ?? undefined}
            encounter={encounter ?? undefined}
            provisionalPatientId={this.props.provisionalPatientId ?? undefined}
          />
        );
      case Primitive.ScheduleSeriesAppointment:
        return (
          <ScheduleSeriesAppointmentRouter
            formField={field}
            navigateToNextPage={this.navigateToNextPage}
            onAppointmentSelected={async (timeSlot, seriesAppointmentCount) =>
              await this.handleSeriesAppointmentSelected(
                timeSlot,
                field,
                seriesAppointmentCount,
              )
            }
            practice={practice ?? undefined}
            encounter={encounter ?? undefined}
            match={this.props.match}
            location={this.props.location}
          />
        );
      case Primitive.PatientSurgicalHistory:
        return (
          <SurgicalHistoryPage
            navigateToNextPage={this.navigateToNextPage}
            field={field}
            setFormField={setFormField}
          />
        );
      case Primitive.VirtualVisitInstructions:
        if (!encounter || !provider || !practice) return <></>;
        return (
          <VirtualVisitPage
            formField={field}
            encounter={encounter}
            provider={provider}
            practice={practice}
            setFormField={setFormField}
            navigateToNextPage={this.navigateToNextPage}
            handleLastPage={this.handleLastPage}
          />
        );
      case Primitive.PatientCareProvidersPicker:
        return (
          <PatientCareProvidersPickerPage
            field={field}
            setFormField={setFormField}
            navigateToNextPage={this.navigateToNextPage}
          />
        );
      default:
        return (
          <Page
            page={page}
            practicePreference={practicePreference}
            formFields={fields}
            formSetId={field.previsitFormSetId}
            navigateToNextPage={this.navigateToNextPage}
            handleLastPage={this.handleLastPage}
          />
        );
    }
  };

  renderPage = () => {
    const { page, errorMsg } = this.state;

    const { pageFields, fields } = this.getPageFields(page);
    if (!pageFields.length) {
      return;
    }

    const firstFieldOfPage = pageFields[0];
    return (
      <>
        {errorMsg && <InfoMessage isError={true}>{errorMsg}</InfoMessage>}
        <div
          className={[
            fullPagePrimitive(firstFieldOfPage.primitive)
              ? styles.FormContentFullWidth
              : styles.FormContent,
          ].join(' ')}
        >
          {this.renderPrimitives(firstFieldOfPage, fields, page)}
        </div>
      </>
    );
  };

  render() {
    const {
      selectedForm,
      selectedProviderId,
      ehrProviderInformation,
      patient,
      provisionalPatientId,
    } = this.props;
    const { page } = this.state;
    const selectedTimeSlot = selectedForm?.fields
      ? getMaybeFormSetSelectedTimeSlot(
          selectedForm.fields,
          page,
          patient ?? undefined,
        )
      : null;
    const selectedProviderWithPractice =
      getMaybeSelectedEhrProviderWithPractice(
        ehrProviderInformation,
        selectedTimeSlot,
        selectedProviderId,
      );

    const hasScheduling = selectedForm?.fields.some(
      (field) => Primitive.ScheduleAppointment === field.primitive,
    );

    return (
      <>
        <LoadingModal isOpen={this.state.isLoading} />
        <NavBar
          view={
            provisionalPatientId
              ? page === 1
                ? View.Login
                : View.Schedule
              : View.Form
          }
          onBackButtonClicked={
            provisionalPatientId
              ? this.navigateToPreviousPageWithContent
              : undefined
          }
        />
        {provisionalPatientId && (
          <Header
            onBackButtonClicked={this.navigateToPreviousPageWithContent}
            page={this.state.page}
            providerConfig={selectedProviderWithPractice}
            selectedEhrTimeSlot={selectedTimeSlot}
            title={hasScheduling ? 'Schedule an appointment' : ''}
          />
        )}
        <div
          className={classNames(
            styles.FormContainer,
            provisionalPatientId ? styles.SchedulingForm : undefined,
          )}
        >
          <div className={styles.Form}>{this.renderPage()}</div>
        </div>
      </>
    );
  }
}

export function mapStateToProps({
  loggedInPatient,
  preferredLanguage,
  formSets,
  selectedFormSetId,
  multiFormSet,
  selectedFormId,
  authenticationCode,
  inbound: {
    ehrProviderInformation,
    selectedEhrTimeSlot,
    selectedProviderId,
    provisionalPatientId,
    isProvisionalPatientReconciled,
  },
}: store.FormSetSelected): StateProps {
  const selectedFormSet = formSets[selectedFormSetId];
  return {
    practicePreference: selectedFormSet.practicePreference,
    patient: loggedInPatient,
    documents: selectedFormSet.documents || [],
    preferredLanguage,
    selectedFormSet: selectedFormSet,
    selectedFormSetId: selectedFormSetId,
    multiFormSet,
    selectedFormId,
    selectedForm: selectedFormSet.forms[selectedFormId],
    formSets,
    authenticationCode,
    ehrProviderInformation: ehrProviderInformation ?? [],
    selectedEhrTimeSlot,
    selectedProviderId,
    provisionalPatientId,
    isProvisionalPatientReconciled,
  };
}

export function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setFormField: (formFieldId: string, value: string) =>
      dispatch(rootReducer.setFormField({ formFieldId, value })),
    setFormSets: (
      formSets: api.PrevisitFormSet.PrevisitFromSetWithPatientData[],
    ) => dispatch(rootReducer.setFormSets({ formSets })),
    setFormStatus: (
      formSetId: string,
      formId: string,
      status: api.PrevisitForm.Status,
    ) => dispatch(rootReducer.setFormStatus({ formSetId, formId, status })),
    setDocuments: (
      formSetId: string,
      documents: api.PrevisitDocument.DocumentWithTemplate[],
    ) => dispatch(rootReducer.setDocuments({ formSetId, documents })),
    setSelectedEhrTimeSlot: (
      selectedEhrTimeSlot: store.SelectedEhrTimeSlot | null,
    ) => dispatch(rootReducer.setSelectedEhrTimeSlot(selectedEhrTimeSlot)),
    setBookedSlot: (bookedSlot: api.EhrTimeSlot.TimeSlotConfirmation | null) =>
      dispatch(rootReducer.setBookedSlot(bookedSlot)),
  };
}

export const Form = withFetchPatient(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withTranslation()(FormComponent)),
);

export const FormMulti = withFetchPatient(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withTranslation()(FormComponent)),
  { multi: true },
);

export const ProvisionalForm = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(FormComponent));
